<template>
  <section class="candidates-table-shell settings-panel">
    <div class="settings-org-section">
      <SettingsSectionHeader
        title="Termination Reasons"
        subtitle="Categorized reasons used when terminating drivers"
        icon="pi-bookmark"
      >
        <template #actions>
          <Button
            label="Add Reason"
            icon="pi pi-plus"
            size="small"
            :disabled="saving"
            @click="openCreate"
          />
          <Button
            icon="pi pi-refresh"
            label="Refresh"
            size="small"
            severity="secondary"
            outlined
            :loading="loading"
            @click="loadReasons"
          />
        </template>
      </SettingsSectionHeader>

      <div class="settings-org-section__body">
        <div v-if="loading && !reasons.length" class="settings-org-section__state">
          <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
        </div>

        <Message
          v-else-if="error && !reasons.length"
          severity="error"
          :closable="false"
          class="settings-org-section__message"
        >
          <div class="flex flex-col sm:flex-row sm:items-center gap-3">
            <span class="flex-1">{{ error }}</span>
            <Button label="Retry" size="small" severity="danger" outlined @click="loadReasons" />
          </div>
        </Message>

        <template v-else>
          <SettingsOrgReferenceToolbar
            v-model:search-query="searchQuery"
            :count-label="countLabel"
            search-placeholder="Search reasons..."
            :loading="loading"
          />

          <div v-if="!filteredReasons.length" class="settings-org-section__empty">
            <i class="pi pi-bookmark settings-org-section__empty-icon" aria-hidden="true" />
            <p>{{ searchQuery ? 'No reasons match your search.' : 'No termination reasons yet.' }}</p>
            <Button
              v-if="searchQuery"
              label="Clear search"
              size="small"
              severity="secondary"
              text
              @click="searchQuery = ''"
            />
          </div>

          <DataTable
            v-else
            :value="filteredReasons"
            size="small"
            dataKey="reasonId"
            sort-mode="single"
            removable-sort
            class="candidates-table"
          >
            <Column field="label" header="Label" sortable style="min-width: 12rem">
              <template #body="{ data }">
                <span class="font-medium">{{ data.label }}</span>
              </template>
            </Column>
            <Column field="category" header="Category" sortable style="min-width: 10rem">
              <template #body="{ data }">
                <Tag :value="data.category" severity="secondary" rounded />
              </template>
            </Column>
            <Column field="sortOrder" header="Sort" sortable style="width: 5rem">
              <template #body="{ data }">
                <span class="text-sm font-mono text-surface-600 dark:text-surface-400">
                  {{ data.sortOrder ?? 0 }}
                </span>
              </template>
            </Column>
            <Column field="active" header="Status" sortable style="width: 7rem">
              <template #body="{ data }">
                <Tag
                  :value="data.active ? 'Active' : 'Inactive'"
                  :severity="data.active ? 'success' : 'secondary'"
                  rounded
                />
              </template>
            </Column>
            <Column header="Actions" style="width: 6rem">
              <template #body="{ data }">
                <div class="crc-table-row-actions">
                  <Button
                    icon="pi pi-pencil"
                    size="small"
                    severity="secondary"
                    outlined
                    rounded
                    v-tooltip.top="'Edit'"
                    :disabled="saving"
                    @click="openEdit(data)"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </template>
      </div>
    </div>

    <TermReasonEditModal
      ref="modalRef"
      v-model:visible="modalVisible"
      :saving="saving"
      :categories="categories"
      @save="onSave"
    />
  </section>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import { useToast } from 'primevue/usetoast';
import SettingsSectionHeader from './SettingsSectionHeader.vue';
import SettingsOrgReferenceToolbar from './SettingsOrgReferenceToolbar.vue';
import TermReasonEditModal from './modals/TermReasonEditModal.vue';
import { useTermReasons } from '../composables/useTermReasons';

defineOptions({ name: 'SettingsTermReasonsSection' });

const toast = useToast();
const modalRef = ref(null);
const modalVisible = ref(false);

const {
  loading,
  saving,
  error,
  reasons,
  categories,
  searchQuery,
  filteredReasons,
  countLabel,
  loadReasons,
  saveReason
} = useTermReasons();

function openCreate() {
  modalRef.value?.openCreate();
}

function openEdit(reason) {
  modalRef.value?.openEdit(reason);
}

async function onSave(payload) {
  try {
    await saveReason(payload);
    modalVisible.value = false;
    toast.add({
      severity: 'success',
      summary: 'Saved',
      detail: payload.reasonId ? 'Termination reason updated.' : 'Termination reason created.',
      life: 2500
    });
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: e?.message || 'Failed to save termination reason.',
      life: 4000
    });
  }
}

onMounted(() => {
  loadReasons();
});

defineExpose({ reload: loadReasons });
</script>
